import React from 'react';
import * as R from 'ramda';
import { pure, compose, withHandlers } from 'react-recompose';
// components
import { ActionsElement, ActionsElement2 } from '../../../components/actions-element';
// features
import PC from '../../permission/role-permission';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// icons
import * as I from '../../../svgs';
//////////////////////////////////////////////////

const iconColor = G.getTheme('colors.dark.blue');

const enhance = compose(
  withHandlers({
    handleEditInvoices: ({ openUpdateInvoices, closeFixedPopup }) => () => {
      openUpdateInvoices();
      closeFixedPopup();
    },
    handleGetXML: ({ data, handleGetXMLRequest, closeFixedPopup }) => () => {
      handleGetXMLRequest({
        masterInvoiceGuid: data.guid,
        fileName: data.masterInvoiceNumber,
      });
      closeFixedPopup();
    },
    handleSendToQuickBook: ({ handleSendToQuickBookRequest, closeFixedPopup }) => () => {
      handleSendToQuickBookRequest();
      closeFixedPopup();
    },
    handleSendToSageIntacctRequest: ({ data, closeFixedPopup, sendToSageIntacctRequest }) => () => {
      sendToSageIntacctRequest(G.getGuidFromObject(data));
      closeFixedPopup();
    },
    handleQBIIFImport: ({ closeFixedPopup, qbIIFImportRequest }) => () => {
      qbIIFImportRequest();
      closeFixedPopup();
    },
    handlePrint: ({ closeFixedPopup, handlePrintMasterInvoice }) => () => {
      handlePrintMasterInvoice();
      closeFixedPopup();
    },
    handlePrintInvoices: ({ closeFixedPopup, handlePrintCloInvoices }) => () => {
      handlePrintCloInvoices();
      closeFixedPopup();
    },
    handleGetTriumphExcel: ({ data, closeFixedPopup, handleGenerateFactoringFileByFactoringType }) => () => {
      handleGenerateFactoringFileByFactoringType({
        masterInvoiceGuid: data.guid,
        fileExtension: GC.EXTENSION_XLSX,
        fileName: data.masterInvoiceNumber,
        factoringType: GC.FACTORING_COMPANY_TYPE_TRIUMPH,
      });
      closeFixedPopup();
    },
    handleGetCompassExcel: ({ data, closeFixedPopup, handleGenerateFactoringFileByFactoringType }) => () => {
      handleGenerateFactoringFileByFactoringType({
        masterInvoiceGuid: data.guid,
        fileExtension: GC.EXTENSION_XLSX,
        fileName: data.masterInvoiceNumber,
        factoringType: GC.FACTORING_COMPANY_TYPE_COMPASS,
      });
      closeFixedPopup();
    },
    handleGetOtrCapitalCsv: ({ data, closeFixedPopup, handleGenerateFactoringFileByFactoringType }) => () => {
      handleGenerateFactoringFileByFactoringType({
        masterInvoiceGuid: data.guid,
        fileExtension: GC.EXTENSION_CSV,
        fileName: data.masterInvoiceNumber,
        factoringType: GC.FACTORING_COMPANY_TYPE_OTR_CAPITAL,
      });
      closeFixedPopup();
    },
    handleGetRTSCsv: ({ data, closeFixedPopup, handleGenerateFactoringFileByFactoringType }) => () => {
      handleGenerateFactoringFileByFactoringType({
        fileExtension: GC.EXTENSION_CSV,
        fileName: data.masterInvoiceNumber,
        masterInvoiceGuid: G.getGuidFromObject(data),
        factoringType: GC.FACTORING_COMPANY_TYPE_RTS_FINANCIAL,
      });

      closeFixedPopup();
    },
    handleShowDocuments: ({ closeFixedPopup, showDocuments }) => () => {
      showDocuments();
      closeFixedPopup();
    },
  }),
  pure,
);

const getOptions = (props) => {
  const {
    configs,
    handlePrint,
    handleGetXML,
    handleShowAudit,
    handleGetRTSCsv,
    handleQBIIFImport,
    handleCustomExport,
    handleEditInvoices,
    handlePrintInvoices,
    handleShowDocuments,
    availableCustomExport,
    handleSendToQuickBook,
    handleGetTriumphExcel,
    handleGetCompassExcel,
    handleGetOtrCapitalCsv,
    availableFactoringTypes,
    handleDeleteMasterInvoice,
    handleSendToSageIntacctRequest,
    handleExportCustomerMasterInvoiceToEDI,
  } = props;

  const { qbAuthorization } = configs;

  const iconStyles = [iconColor, 15, 17];
  const factoringType = G.getPropFromObject('factoringType', availableFactoringTypes);

  const permissions = [
    PC.CLO_MASTER_INVOICE_WRITE,
    PC.CLO_MASTER_INVOICE_OVERWRITE_EXECUTE,
  ];

  const options = [
    {
      action: handleEditInvoices,
      frontIcon: I.pencil(iconColor),
      text: G.getWindowLocale('actions:update-invoices', 'Update Invoices'),
      permissions: G.ifElse(
        G.isTrue(R.path(['data', 'completed'], props)),
        [PC.CLO_MASTER_INVOICE_OVERWRITE_EXECUTE],
        permissions,
      ),
    },
    {
      permissions,
      action: handleGetXML,
      frontIcon: I.downloadDocument(...iconStyles),
      text: G.getWindowLocale('actions:get-xml-file', 'Get XML File'),
    },
    {
      action: handlePrint,
      frontIcon: I.printer(iconColor),
      text: G.getWindowLocale('actions:print-master-invoice', 'Print Master Invoice'),
    },
    {
      permissions,
      action: handlePrintInvoices,
      frontIcon: I.printer(iconColor),
      text: G.getWindowLocale('actions:print-invoices', 'Print Invoices'),
    },
    {
      permissions,
      action: handleSendToQuickBook,
      frontIcon: I.quickbook(iconColor),
      disabled: G.isNilOrEmpty(qbAuthorization),
      text: G.getWindowLocale('actions:export-to-QB', 'Export to QuickBooks'),
    },
    {
      permissions,
      frontIcon: I.gear(iconColor, 16, 16),
      action: handleSendToSageIntacctRequest,
      text: G.getWindowLocale('actions:send-to-sage-intacct', 'Send To Sage Intacct'),
    },
    {
      permissions,
      action: handleQBIIFImport,
      frontIcon: I.quickbook(iconColor, 16, 16),
      text: G.getWindowLocale('actions:qb-iif-export', 'QuickBooks IIF Export'),
    },
    {
      permissions,
      frontIcon: I.gear(iconColor, 16, 16),
      action: handleExportCustomerMasterInvoiceToEDI,
      text: G.getWindowLocale('actions:export-to-edi', 'Export to EDI'),
    },
    {
      action: handleCustomExport,
      disabled: R.not(availableCustomExport),
      frontIcon: I.downloadDocument(...iconStyles),
      text: G.getWindowLocale('actions:custom-export', 'Custom Export'),
    },
    {
      permissions,
      action: handleGetTriumphExcel,
      frontIcon: I.downloadDocument(...iconStyles),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_TRIUMPH),
      text: G.getWindowLocale('actions:generate-triumph-excel', 'Generate Triumph Excel'),
    },
    {
      permissions,
      action: handleGetCompassExcel,
      frontIcon: I.downloadDocument(...iconStyles),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_COMPASS),
      text: G.getWindowLocale('actions:generate-compass-excel', 'Generate Compass Excel'),
    },
    {
      permissions,
      action: handleGetOtrCapitalCsv,
      frontIcon: I.downloadDocument(...iconStyles),
      text: G.getWindowLocale('actions:otr-capital-csv', 'OTR Capital CSV'),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_OTR_CAPITAL),
    },
    {
      permissions,
      action: handleGetRTSCsv,
      frontIcon: I.downloadDocument(...iconStyles),
      text: G.getWindowLocale('actions:rts-csv', 'RTS Financial CSV'),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_RTS_FINANCIAL),
    },
    {
      action: handleShowDocuments,
      frontIcon: I.documents(iconColor, 15, 17),
      text: G.getWindowLocale('actions:show-documents', 'Show Documents'),
    },
    {
      action: handleShowAudit,
      text: G.getWindowLocale('titles:audit', 'Audit'),
      frontIcon: I.renderAuditHistoryIcon(iconColor, 15, 17),
    },
    {
      frontIcon: I.trash(iconColor),
      action: handleDeleteMasterInvoice,
      permissions: [PC.CLO_MASTER_INVOICE_DELETE_EXECUTE],
      text: G.getWindowLocale('actions:delete', 'Delete'),
    },
  ];

  return R.filter(
    ({ disabled }) => R.not(disabled),
    options,
  );
};

const getHeaderOptions = (props) => {
  const {
    handleShowAudit,
    handleGetXMLRequest,
    handlePrintInvoices,
    handlePrintMasterInvoice,
  } = props;

  const iconStyles = [iconColor, 15, 17];

  const options = [
    {
      iconMl: 10,
      withTitle: true,
      frontIcon: I.printer(iconColor),
      action: handlePrintMasterInvoice,
      text: G.getWindowLocale('actions:print-master-invoice', 'Print Master Invoice'),
    },
    {
      iconMl: 10,
      withTitle: true,
      textStyle: { ml: 10 },
      action: handlePrintInvoices,
      frontIcon: I.printer(iconColor),
      text: G.getWindowLocale('actions:print-invoices', 'Print Invoices'),
    },
    {
      action: handleGetXMLRequest,
      frontIcon: I.downloadTemplate(...iconStyles),
      text: G.getWindowLocale('actions:get-xml-file', 'Get XML File'),
    },
    {
      action: handleShowAudit,
      text: G.getWindowLocale('titles:audit', 'Audit'),
      frontIcon: I.renderAuditHistoryIcon(iconColor, 15, 17),
    },
  ];

  return options;
};

const getHeaderOtherOptions = (props) => {
  const {
    configs,
    handleGetRTSCsv,
    handleQBIIFImport,
    handleEditInvoices,
    handleCustomExport,
    availableCustomExport,
    handleSendToQuickBook,
    handleGetTriumphExcel,
    handleGetCompassExcel,
    handleGetOtrCapitalCsv,
    availableFactoringTypes,
    handleSendToSageIntacctRequest,
    handleExportCustomerMasterInvoiceToEDI,
  } = props;

  const { qbAuthorization } = configs;

  const iconStyles = [iconColor, 15, 17];
  const factoringType = G.getPropFromObject('factoringType', availableFactoringTypes);

  const permissions = [
    PC.CLO_MASTER_INVOICE_WRITE,
    PC.CLO_MASTER_INVOICE_OVERWRITE_EXECUTE,
  ];

  const options = [
    {
      action: handleEditInvoices,
      frontIcon: I.pencil(iconColor),
      text: G.getWindowLocale('actions:update-invoices', 'Update Invoices'),
      permissions: G.ifElse(
        G.isTrue(R.path(['data', 'completed'], props)),
        [PC.CLO_MASTER_INVOICE_OVERWRITE_EXECUTE],
        permissions,
      ),
    },
    {
      permissions,
      action: handleSendToQuickBook,
      frontIcon: I.quickbook(iconColor),
      disabled: G.isNilOrEmpty(qbAuthorization),
      text: G.getWindowLocale('actions:export-to-QB', 'Export to QuickBooks'),
    },
    {
      permissions,
      frontIcon: I.gear(iconColor, 16, 16),
      action: handleSendToSageIntacctRequest,
      text: G.getWindowLocale('actions:send-to-sage-intacct', 'Send To Sage Intacct'),
    },
    {
      permissions,
      action: handleQBIIFImport,
      frontIcon: I.quickbook(iconColor, 16, 16),
      text: G.getWindowLocale('actions:qb-iif-export', 'QuickBooks IIF Export'),
    },
    {
      permissions,
      frontIcon: I.gear(iconColor, 16, 16),
      action: handleExportCustomerMasterInvoiceToEDI,
      text: G.getWindowLocale('actions:export-to-edi', 'Export to EDI'),
    },
    {
      action: handleCustomExport,
      disabled: R.not(availableCustomExport),
      frontIcon: I.downloadDocument(...iconStyles),
      text: G.getWindowLocale('actions:custom-export', 'Custom Export'),
    },
    {
      permissions,
      action: handleGetTriumphExcel,
      frontIcon: I.downloadDocument(...iconStyles),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_TRIUMPH),
      text: G.getWindowLocale('actions:generate-triumph-excel', 'Generate Triumph Excel'),
    },
    {
      permissions,
      action: handleGetCompassExcel,
      frontIcon: I.downloadDocument(...iconStyles),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_COMPASS),
      text: G.getWindowLocale('actions:generate-compass-excel', 'Generate Compass Excel'),
    },
    {
      permissions,
      action: handleGetOtrCapitalCsv,
      frontIcon: I.downloadDocument(...iconStyles),
      text: G.getWindowLocale('actions:otr-capital-csv', 'OTR Capital CSV'),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_OTR_CAPITAL),
    },
    {
      permissions,
      action: handleGetRTSCsv,
      frontIcon: I.downloadDocument(...iconStyles),
      text: G.getWindowLocale('actions:rts-csv', 'RTS Financial CSV'),
      disabled: G.notEquals(factoringType, GC.FACTORING_COMPANY_TYPE_RTS_FINANCIAL),
    },
  ];

  return R.filter(
    ({ disabled }) => R.not(disabled),
    options,
  );
};

const HeaderActions = (props) => <ActionsElement2 options={getHeaderOptions(props)} />;

const OtherHeaderActions = enhance((props) => (
  <ActionsElement options={getHeaderOtherOptions(props)} />
));

const RowActions = enhance((props) => (
  <ActionsElement options={getOptions(props)} />
));

export {
  RowActions,
  HeaderActions,
  OtherHeaderActions,
};
